import React, { useEffect } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "../../../../entrypoints/theme";
import Button from "@src/stories/elements/Button";
import ImageComponent from "@project-stories/elements/Image/Image";
import { sendGtagEvent } from "frontend/js/helpers";

let hasLoaded = false;

export const PromotionTile = ({ promotion, sectionSettings }) => {
    const isMobile = useMobileBreakpoint(mobileBreakpoint);
    
    // Generate the column span class for mobile
    const getColumnSpanClass = () => {
        const mobileSpan = promotion.col_span_mobile || 1;
        const columnClass = mobileSpan > 1 ? `span-${mobileSpan}` : '';
        
        return columnClass;
    };

    const getAspectRatioClass = () => {
        const mobileSpan = promotion.col_span_mobile || 1;
        
        if (mobileSpan === 1) {
            return 'pt-[150%] md:pt-[150%]';
        } else {
            return 'pt-[100%] md:pt-[150%]';
        }
    };

    // Process promotional link to support various URL formats
    const getProcessedLink = () => {
        const link = promotion.link;
        
        if (!link) return '#';
        
        if (link.startsWith('http://') || link.startsWith('https://')) {
            return link;
        }
        
        if (link.startsWith('/')) {
            return link;
        }

        if (!link.includes('/') && !link.includes('.')) {
            return `/collections/${link}`;
        }

        if (link.includes('.') && !link.includes('/')) {
            return `https://${link}`;
        }

        if (link.includes('/')) {
            const parts = link.split('/');
            const type = parts[0];
            const handle = parts.slice(1).join('/');
            
            switch (type) {
                case 'collections':
                    return `/collections/${handle}`;
                case 'products':
                    return `/products/${handle}`;
                case 'pages':
                    return `/pages/${handle}`;
                case 'blogs':
                    return `/blogs/${handle}`;
                default:
                    return `/${link}`;
            }
        }
        
        return `/collections/${link}`;
    };

    const getContentAlignmentClasses = () => {
        const verticalPositionMobile = sectionSettings?.promo_content_vertical_position_mobile || promotion.settings?.content_vertical_position_mobile || 'center';
        const verticalPositionDesktop = sectionSettings?.promo_content_vertical_position_desktop || promotion.settings?.content_vertical_position_desktop || 'center';
        
        const horizontalPositionMobile = sectionSettings?.promo_content_horizontal_position_mobile || promotion.settings?.content_horizontal_position_mobile || 'center';
        const horizontalPositionDesktop = sectionSettings?.promo_content_horizontal_position_desktop || promotion.settings?.content_horizontal_position_desktop || 'center';

        const justifyContentClasses = {
            'start': 'justify-start',
            'center': 'justify-center',
            'end': 'justify-end'
        };

        const alignItemsClasses = {
            'start': 'items-start',
            'center': 'items-center', 
            'end': 'items-end'
        };

        const mobileJustifyClass = justifyContentClasses[verticalPositionMobile] || 'justify-center';
        const mobileAlignClass = alignItemsClasses[horizontalPositionMobile] || 'items-center';
        const desktopJustifyClass = justifyContentClasses[verticalPositionDesktop] || 'justify-center';
        const desktopAlignClass = alignItemsClasses[horizontalPositionDesktop] || 'items-center';

        return `${mobileJustifyClass} lg:${desktopJustifyClass} ${mobileAlignClass} lg:${desktopAlignClass}`;
    };

    function handleClick (e) {
        e.preventDefault();

        sendGtagEvent(
            'select_promotion',
            promotion?.handle,
            promotion?.promotional_name,
            'promotional_tile',
            getProcessedLink()
        )
    }

    useEffect(() => {
        if (hasLoaded) return

        hasLoaded = true;

        sendGtagEvent(
            'view_promotion',
            promotion.handle,
            promotion.promotional_name,
            'promotional_tile'
        )
    })
      
    return (
        <div
            className={`promotion-tile relative ${getColumnSpanClass()}`}
            style={{color: promotion.settings?.text_colour || 'white' }}
        >
            <div className={`relative ${getAspectRatioClass()}`}>
                <div className="absolute top-0 left-0 right-0 h-full">
                    <ImageComponent
                        imageUrl={
                            isMobile
                                ? (promotion?.mobile_image || promotion.image)
                                : promotion.image
                            }
                        isLazy={true}
                        classes={'object-cover h-full w-full'}
                    />
                    <div 
                        className={`category-block__content flex flex-col h-full w-full absolute bottom-0 left-0 p-[32px] ${getContentAlignmentClasses()}`}
                        style={{
                            rowGap: isMobile 
                                ? `${sectionSettings?.promo_content_gap_mobile || promotion.settings?.content_gap_mobile || 4}px`
                                : `${sectionSettings?.promo_content_gap_desktop || promotion.settings?.content_gap_desktop || 8}px`
                        }}
                    >
                        <div className="category-block__content--subtitle">
                            {isMobile &&
                                <h5 className={`${promotion.settings?.subtitle_font_class_mobile}`}>{promotion.subtitle}</h5>
                            }
                            {!isMobile &&
                                <h5 className={`${promotion.settings?.subtitle_font_class_desktop}`}>{promotion.subtitle}</h5>
                            }
                        </div>
                        <div className="category-block__content--title">
                            {isMobile &&
                                <h4 className={`${promotion.settings?.title_font_class_mobile}`}>{promotion.title}</h4>
                            }
                            {!isMobile &&
                                <h4 className={`${promotion.settings?.title_font_class_desktop}`}>{promotion.title}</h4>
                            }
                        </div>
                        {isMobile && promotion.link && promotion.link_label &&
                            <a
                                href={getProcessedLink()} className={`no-underline max-w-max button ${promotion.settings?.button_style_mobile || 'btn-outline-light'} hover:no-underline`}
                                onClick={(e) => handleClick(e)}
                            >
                                {promotion.link_label}
                            </a>
                            
                        }
                        {!isMobile && promotion.link && promotion.link_label &&
                            <a
                                href={getProcessedLink()} className={`no-underline max-w-max button ${promotion.settings?.button_style_desktop || 'btn-outline-light'} hover:no-underline`}
                                onClick={(e) => handleClick(e)}
                            >
                                {promotion.link_label}
                            </a>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromotionTile;
